import React, { useState, useMemo, useCallback } from 'react';
import {
  FolderIcon,
  FolderPlusIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  TagIcon,
  PlusIcon,
  FunnelIcon,
  PencilSquareIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  ArrowSmallDownIcon,
  ArrowSmallUpIcon,
} from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';
import Modal from '../components/common/Modal';
import LoadingScreen from '../components/common/LoadingScreen';
import { useLanguage } from '../contexts/LanguageContext';
import { useDocumentFolders } from '../hooks/useDocumentFolders';
import { useDocumentStorage } from '../hooks/useDocumentStorage';
import { useDocumentStorageCategories } from '../hooks/useDocumentStorageCategories';
import { useCustomers } from '../hooks/useCustomers';
import { FolderTreeNode, DocumentStorage, DocumentStorageFormData } from '../types/documentStorage';

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (date?: Date | null): string => {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getFileIcon = (fileType?: string) => {
  if (fileType?.includes('pdf')) return '📄';
  if (fileType?.includes('image')) return '🖼️';
  if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
  if (fileType?.includes('sheet') || fileType?.includes('excel')) return '📊';
  if (fileType?.includes('zip')) return '🗜️';
  return '📎';
};

const DocumentLibrary: React.FC = () => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const { categories, createCategory, updateCategory, deleteCategory } = useDocumentStorageCategories();

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<FolderTreeNode | null>(null);
  const [docToDelete, setDocToDelete] = useState<DocumentStorage | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState<string>('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadCustomer, setUploadCustomer] = useState('general');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentStorage | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCategoryManageModal, setShowCategoryManageModal] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [editingCategoryColor, setEditingCategoryColor] = useState('#6366f1');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#6366f1');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [uploadTags, setUploadTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDoc, setEditDoc] = useState<DocumentStorage | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveDoc, setMoveDoc] = useState<DocumentStorage | null>(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState<string>('');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState('');
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
  const [moveFolderNode, setMoveFolderNode] = useState<FolderTreeNode | null>(null);
  const [moveFolderTarget, setMoveFolderTarget] = useState<string>('');

  const {
    folderTree,
    loading: foldersLoading,
    createFolder,
    updateFolder,
    deleteFolder,
  } = useDocumentFolders();

  const filters = useMemo(() => ({
    folderId: selectedFolderId === 'root' ? undefined : selectedFolderId ?? undefined,
    searchTerm: searchQuery || undefined,
    status: 'active' as const,
    categoryId: filterCategory || undefined,
    tags: filterTag ? [filterTag] : undefined,
  }), [selectedFolderId, searchQuery, filterCategory, filterTag]);

  const {
    documents,
    loading: docsLoading,
    createDocument,
    updateDocument,
    deleteDocument,
  } = useDocumentStorage(filters);

  const loading = foldersLoading || docsLoading;

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleSelectFolder = (folderId: string | null) => {
    setSelectedFolderId(folderId);
    setMobileSidebarOpen(false);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await createFolder({
        name: newFolderName.trim(),
        parentId: newFolderParent || (selectedFolderId && selectedFolderId !== 'root' ? selectedFolderId : undefined),
        color: '#6366f1',
        icon: 'FolderIcon',
      });
      setNewFolderName('');
      setNewFolderParent('');
      setShowFolderModal(false);
    } catch (err) {
      console.error('Error creating folder:', err);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      await deleteFolder(folderToDelete.id);
      if (selectedFolderId === folderToDelete.id) setSelectedFolderId(null);
      setShowDeleteFolderModal(false);
      setFolderToDelete(null);
    } catch (err) {
      console.error('Error deleting folder:', err);
    }
  };

  const handleStartRenameFolder = (node: FolderTreeNode) => {
    setEditingFolderId(node.id);
    setEditingFolderName(node.name);
  };

  const handleSaveRenameFolder = async () => {
    if (!editingFolderId || !editingFolderName.trim()) return;
    try {
      await updateFolder(editingFolderId, { name: editingFolderName.trim() });
      setEditingFolderId(null);
      setEditingFolderName('');
    } catch (err) {
      console.error('Error renaming folder:', err);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle.trim()) return;
    setUploading(true);
    try {
      const data: DocumentStorageFormData = {
        title: uploadTitle.trim(),
        categoryId: uploadCategory || categories[0]?.id || '',
        customerId: uploadCustomer,
        isPublic: false,
        file: uploadFile,
        folderId: selectedFolderId && selectedFolderId !== 'root' ? selectedFolderId : undefined,
        tags: uploadTags.length > 0 ? uploadTags : undefined,
      };
      await createDocument(data);
      setUploadTitle('');
      setUploadFile(null);
      setUploadCategory('');
      setUploadCustomer('general');
      setUploadTags([]);
      setTagInput('');
      setShowUploadModal(false);
    } catch (err) {
      console.error('Error uploading document:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async () => {
    if (!docToDelete) return;
    try {
      await deleteDocument(docToDelete.id);
      setShowDeleteDocModal(false);
      setDocToDelete(null);
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDownload = (doc: DocumentStorage) => {
    if (doc.downloadUrl) {
      window.open(doc.downloadUrl, '_blank');
    }
  };

  const handlePreview = (doc: DocumentStorage) => {
    setPreviewDoc(doc);
    setShowPreviewModal(true);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await createCategory({
        name: newCategoryName.trim(),
        color: newCategoryColor,
        icon: 'FolderIcon',
      });
      setNewCategoryName('');
      setNewCategoryColor('#6366f1');
      setShowCategoryModal(false);
    } catch (err) {
      console.error('Error creating category:', err);
    }
  };

  const handleStartEditCategory = (cat: { id: string; name: string; color: string }) => {
    setEditingCategoryId(cat.id);
    setEditingCategoryName(cat.name);
    setEditingCategoryColor(cat.color || '#6366f1');
  };

  const handleSaveEditCategory = async () => {
    if (!editingCategoryId || !editingCategoryName.trim()) return;
    try {
      await updateCategory(editingCategoryId, {
        name: editingCategoryName.trim(),
        color: editingCategoryColor,
      });
      setEditingCategoryId(null);
      setEditingCategoryName('');
      setEditingCategoryColor('#6366f1');
    } catch (err) {
      console.error('Error updating category:', err);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteCategory(id);
      if (filterCategory === id) setFilterCategory('');
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !uploadTags.includes(tag)) {
      setUploadTags([...uploadTags, tag]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag: string) => {
    setUploadTags(uploadTags.filter(t => t !== tag));
  };

  const handleStartEdit = (doc: DocumentStorage) => {
    setEditDoc(doc);
    setEditTitle(doc.title);
    setEditCategory(doc.category?.id || '');
    setEditTags(doc.tags || []);
    setEditTagInput('');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editDoc || !editTitle.trim()) return;
    try {
      await updateDocument(editDoc.id, {
        title: editTitle.trim(),
        categoryId: editCategory || categories[0]?.id || '',
        tags: editTags.length > 0 ? editTags : undefined,
      });
      setShowEditModal(false);
      setEditDoc(null);
    } catch (err) {
      console.error('Error editing document:', err);
    }
  };

  const handleAddEditTag = () => {
    const tag = editTagInput.trim();
    if (tag && !editTags.includes(tag)) {
      setEditTags([...editTags, tag]);
    }
    setEditTagInput('');
  };

  const handleRemoveEditTag = (tag: string) => {
    setEditTags(editTags.filter(t => t !== tag));
  };

  const handleStartMove = (doc: DocumentStorage) => {
    setMoveDoc(doc);
    setMoveTargetFolder(doc.folderId || '');
    setShowMoveModal(true);
  };

  const handleSaveMove = async () => {
    if (!moveDoc) return;
    try {
      await updateDocument(moveDoc.id, {
        folderId: moveTargetFolder || undefined,
      } as any);
      setShowMoveModal(false);
      setMoveDoc(null);
    } catch (err) {
      console.error('Error moving document:', err);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedDocs.size === sortedDocuments.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(sortedDocuments.map(d => d.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(Array.from(selectedDocs).map(id => deleteDocument(id)));
      setSelectedDocs(new Set());
      setShowBulkDeleteModal(false);
    } catch (err) {
      console.error('Error bulk deleting documents:', err);
    }
  };

  const handleSortToggle = (field: 'name' | 'date' | 'size') => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const handleStartMoveFolder = (node: FolderTreeNode) => {
    setMoveFolderNode(node);
    setMoveFolderTarget(node.parentId || '');
    setShowMoveFolderModal(true);
  };

  const handleSaveMoveFolder = async () => {
    if (!moveFolderNode) return;
    try {
      await updateFolder(moveFolderNode.id, { parentId: moveFolderTarget || undefined } as any);
      setShowMoveFolderModal(false);
      setMoveFolderNode(null);
    } catch (err) {
      console.error('Error moving folder:', err);
    }
  };

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    documents.forEach(doc => doc.tags?.forEach(tag => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [documents]);

  const sortedDocuments = useMemo(() => {
    let docs = [...documents];
    if (filterType) {
      docs = docs.filter(doc => {
        if (filterType === 'pdf') return doc.fileType?.includes('pdf');
        if (filterType === 'image') return doc.fileType?.includes('image');
        if (filterType === 'doc') return doc.fileType?.includes('word') || doc.fileType?.includes('document') || doc.fileType?.includes('sheet') || doc.fileType?.includes('excel');
        return true;
      });
    }
    docs.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.title.localeCompare(b.title);
      else if (sortBy === 'date') {
        const aDate = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt || 0).getTime();
        const bDate = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt || 0).getTime();
        cmp = aDate - bDate;
      }
      else if (sortBy === 'size') cmp = (a.fileSize || 0) - (b.fileSize || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return docs;
  }, [documents, sortBy, sortDir, filterType]);

  const totalStorageSize = useMemo(() => {
    return documents.reduce((sum, doc) => sum + (doc.fileSize || 0), 0);
  }, [documents]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const next = prev - 1;
      if (next <= 0) setIsDragging(false);
      return next;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(0);
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadFile(file);
      if (!uploadTitle.trim()) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setUploadTitle(nameWithoutExt);
      }
    }
  }, [uploadTitle]);

  const renderFolderOptions = (nodes: FolderTreeNode[], level = 0): React.ReactNode => {
    return nodes.flatMap(node => [
      <option key={node.id} value={node.id}>
        {'  '.repeat(level)}{node.name}
      </option>,
      ...(node.children?.length > 0 ? [renderFolderOptions(node.children, level + 1)] : []),
    ]);
  };

  const renderFolderTree = (nodes: FolderTreeNode[], level = 0): React.ReactNode => {
    return nodes.map(node => (
      <div key={node.id}>
        {editingFolderId === node.id ? (
          <div
            className="flex items-center gap-1 px-2 py-1.5 rounded-md"
            style={{ paddingLeft: `${0.5 + level * 1}rem` }}
          >
            <span className="w-4 flex-shrink-0" />
            <FolderIcon className="w-4 h-4 flex-shrink-0 text-indigo-500" />
            <input
              type="text"
              value={editingFolderName}
              onChange={(e) => setEditingFolderName(e.target.value)}
              onBlur={handleSaveRenameFolder}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRenameFolder();
                if (e.key === 'Escape') { setEditingFolderId(null); setEditingFolderName(''); }
              }}
              className="flex-1 px-1.5 py-0.5 text-xs sm:text-sm rounded border border-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              autoFocus
            />
          </div>
        ) : (
          <div
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors text-xs sm:text-sm group ${
              selectedFolderId === node.id
                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
            }`}
            style={{ paddingLeft: `${0.5 + level * 1}rem` }}
            onClick={() => handleSelectFolder(node.id)}
          >
            {node.children?.length > 0 ? (
              <button
                onClick={(e) => { e.stopPropagation(); toggleFolder(node.id); }}
                className="flex-shrink-0 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {expandedFolders.has(node.id) ? (
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                )}
              </button>
            ) : (
              <span className="w-4 flex-shrink-0" />
            )}
            <FolderIcon className="w-4 h-4 flex-shrink-0 text-indigo-500" />
            <span className="truncate flex-1">{node.name}</span>
            {node.documentCount > 0 && (
              <span className="text-[10px] text-gray-400 flex-shrink-0">{node.documentCount}</span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); handleStartRenameFolder(node); }}
              className="flex-shrink-0 p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/30 opacity-0 group-hover:opacity-100"
            >
              <PencilSquareIcon className="w-3 h-3 text-indigo-400" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleStartMoveFolder(node); }}
              className="flex-shrink-0 p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/30 opacity-0 group-hover:opacity-100"
            >
              <ArrowsRightLeftIcon className="w-3 h-3 text-indigo-400" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setFolderToDelete(node); setShowDeleteFolderModal(true); }}
              className="flex-shrink-0 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 opacity-0 group-hover:opacity-100"
            >
              <TrashIcon className="w-3 h-3 text-red-400" />
            </button>
          </div>
        )}
        {expandedFolders.has(node.id) && node.children?.length > 0 && (
          renderFolderTree(node.children, level + 1)
        )}
      </div>
    ));
  };

  const totalDocs = sortedDocuments.length;

  return (
    <div>
      <CommonPageHeader
        title={t('documentStorage.title')}
        subtitle={t('documentStorage.subtitle')}
        icon={FolderIcon}
      >
        <button
          onClick={() => setShowFolderModal(true)}
          className="inline-flex items-center px-2 sm:px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-xs sm:text-sm whitespace-nowrap"
        >
          <FolderPlusIcon className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">{t('documentStorage.actions.newFolder')}</span>
        </button>
        <button
          onClick={() => setShowCategoryModal(true)}
          className="inline-flex items-center px-2 sm:px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-xs sm:text-sm whitespace-nowrap"
        >
          <PlusIcon className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">{t('documentStorage.actions.newCategory')}</span>
        </button>
        <button
          onClick={() => setShowCategoryManageModal(true)}
          className="inline-flex items-center px-2 sm:px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-xs sm:text-sm whitespace-nowrap"
        >
          <TagIcon className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">{t('documentStorage.categories.title')}</span>
        </button>
        <button
          onClick={() => setShowUploadModal(true)}
          className="inline-flex items-center px-2 sm:px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-xs sm:text-sm whitespace-nowrap"
        >
          <ArrowUpTrayIcon className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">{t('documentStorage.actions.upload')}</span>
        </button>
      </CommonPageHeader>

      <div className="-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        {loading ? (
          <LoadingScreen />
        ) : (
          <div className="flex gap-4">
            {/* Sidebar - Desktop */}
            <div className="hidden md:block w-64 flex-shrink-0">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-3 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                  <h3 className="text-sm font-semibold">{t('documentStorage.folders.allDocuments')}</h3>
                </div>
                <div className="p-2 space-y-0.5 max-h-[calc(100vh-12rem)] overflow-y-auto">
                  <div
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${
                      selectedFolderId === null || selectedFolderId === 'root'
                        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                    }`}
                    onClick={() => handleSelectFolder(null)}
                  >
                    <HomeIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">{t('documentStorage.folders.root')}</span>
                    <span className="text-xs text-gray-400">{totalDocs}</span>
                  </div>
                  <div>
                    {renderFolderTree(folderTree)}
                  </div>
                  {folderTree.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-4">{t('documentStorage.folders.empty')}</p>
                  )}
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{t('documentStorage.actions.storageUsage')}</span>
                      <span className="font-medium">{formatFileSize(totalStorageSize)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Mobile drawer */}
            {mobileSidebarOpen && (
              <div className="fixed inset-0 z-50 md:hidden">
                <div className="absolute inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
                <div className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-gray-800 shadow-xl overflow-y-auto">
                  <div className="flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <h3 className="text-sm font-semibold">{t('documentStorage.folders.allDocuments')}</h3>
                    <button onClick={() => setMobileSidebarOpen(false)}>
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-2 space-y-0.5">
                    <div
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm ${
                        selectedFolderId === null || selectedFolderId === 'root'
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => handleSelectFolder(null)}
                    >
                      <HomeIcon className="w-4 h-4" />
                      <span className="flex-1">{t('documentStorage.folders.root')}</span>
                    </div>
                    {renderFolderTree(folderTree)}
                  </div>
                </div>
              </div>
            )}

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Search bar + mobile folder toggle + filters */}
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => setMobileSidebarOpen(true)}
                  className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                >
                  <FolderIcon className="w-4 h-4" />
                </button>
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('documentStorage.actions.search')}
                    className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
                    showFilters || filterCategory || filterTag
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  <FunnelIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-1.5">
                    <TagIcon className="w-4 h-4 text-gray-400" />
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">{t('documentStorage.actions.allCategories')}</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  {allTags.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <select
                        value={filterTag}
                        onChange={(e) => setFilterTag(e.target.value)}
                        className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">{t('documentStorage.actions.allTags')}</option>
                        {allTags.map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {(filterCategory || filterTag) && (
                    <button
                      onClick={() => { setFilterCategory(''); setFilterTag(''); }}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      {t('documentStorage.actions.cancel')}
                    </button>
                  )}
                </div>
              )}

              {/* Sort bar + bulk actions + storage usage */}
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  {sortedDocuments.length > 0 && (
                    <button
                      onClick={handleSelectAll}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <CheckCircleIcon className={`w-4 h-4 ${selectedDocs.size === sortedDocuments.length && selectedDocs.size > 0 ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <span className="hidden sm:inline">{selectedDocs.size === sortedDocuments.length && selectedDocs.size > 0 ? t('documentStorage.actions.deselectAll') : t('documentStorage.actions.selectAll')}</span>
                    </button>
                  )}
                  {selectedDocs.size > 0 && (
                    <button
                      onClick={() => setShowBulkDeleteModal(true)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                      <span>{t('documentStorage.actions.bulkDelete')} ({selectedDocs.size})</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">{t('documentStorage.actions.allTypes')}</option>
                    <option value="pdf">{t('documentStorage.actions.typePdf')}</option>
                    <option value="image">{t('documentStorage.actions.typeImage')}</option>
                    <option value="doc">{t('documentStorage.actions.typeDoc')}</option>
                  </select>
                  <div className="flex items-center gap-0.5">
                    {(['name', 'date', 'size'] as const).map(field => (
                      <button
                        key={field}
                        onClick={() => handleSortToggle(field)}
                        className={`inline-flex items-center gap-0.5 px-2 py-1 text-xs rounded-lg transition-colors ${
                          sortBy === field
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {t(`documentStorage.actions.sort${field.charAt(0).toUpperCase() + field.slice(1)}`)}
                        {sortBy === field && (
                          sortDir === 'asc' ? <ArrowSmallUpIcon className="w-3 h-3" /> : <ArrowSmallDownIcon className="w-3 h-3" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Document list */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {sortedDocuments.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('documentStorage.documents.empty')}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('documentStorage.documents.emptyHint')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {sortedDocuments.map(doc => (
                      <div
                        key={doc.id}
                        className={`flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${selectedDocs.has(doc.id) ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                      >
                        <button
                          onClick={() => handleToggleSelect(doc.id)}
                          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selectedDocs.has(doc.id)
                              ? 'bg-indigo-600 border-indigo-600'
                              : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400'
                          }`}
                        >
                          {selectedDocs.has(doc.id) && <CheckCircleIcon className="w-4 h-4 text-white" />}
                        </button>
                        <span className="text-xl flex-shrink-0">{getFileIcon(doc.fileType)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{doc.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex-wrap">
                            {doc.category?.name && (
                              <span
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                                style={{ backgroundColor: doc.category.color }}
                              >
                                {doc.category.name}
                              </span>
                            )}
                            {doc.customerName && (
                              <span className="truncate hidden sm:inline">{doc.customerName}</span>
                            )}
                            <span className="flex-shrink-0">{formatFileSize(doc.fileSize)}</span>
                            <span className="flex-shrink-0 hidden sm:inline">{formatDate(doc.createdAt)}</span>
                            {doc.tags && doc.tags.length > 0 && (
                              <div className="flex items-center gap-1 flex-wrap">
                                {doc.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[10px] font-medium">
                                    {tag}
                                  </span>
                                ))}
                                {doc.tags.length > 3 && (
                                  <span className="text-[10px] text-gray-400">+{doc.tags.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleStartEdit(doc)}
                            className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-gray-400 hover:text-indigo-600 transition-colors"
                            title={t('documentStorage.actions.edit')}
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleStartMove(doc)}
                            className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-gray-400 hover:text-indigo-600 transition-colors"
                            title={t('documentStorage.actions.move')}
                          >
                            <ArrowsRightLeftIcon className="w-4 h-4" />
                          </button>
                          {doc.downloadUrl && (
                            <button
                              onClick={() => handlePreview(doc)}
                              className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-gray-400 hover:text-indigo-600 transition-colors"
                              title={t('documentStorage.actions.preview')}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                          )}
                          {doc.downloadUrl && (
                            <button
                              onClick={() => handleDownload(doc)}
                              className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-gray-400 hover:text-indigo-600 transition-colors"
                              title={t('documentStorage.actions.download')}
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => { setDocToDelete(doc); setShowDeleteDocModal(true); }}
                            className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 transition-colors"
                            title={t('documentStorage.actions.delete')}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Folder Modal */}
      <Modal isOpen={showFolderModal} onClose={() => setShowFolderModal(false)} title={t('documentStorage.folders.create')} size="sm">
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.folders.name')}
            </label>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('documentStorage.folders.namePlaceholder')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.folders.parent')}
            </label>
            <select
              value={newFolderParent}
              onChange={(e) => setNewFolderParent(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('documentStorage.folders.root')}</option>
              {renderFolderOptions(folderTree)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowFolderModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {t('documentStorage.actions.save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title={t('documentStorage.upload.title')} size="md">
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.upload.titleField')}
            </label>
            <input
              type="text"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder={t('documentStorage.upload.titlePlaceholder')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.upload.categoryField')}
            </label>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('documentStorage.upload.noCategory')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.upload.customerField')}
            </label>
            <select
              value={uploadCustomer}
              onChange={(e) => setUploadCustomer(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="general">{t('documentStorage.upload.general')}</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c.type === 'organization' ? c.companyName : `${c.firstName} ${c.lastName}`.trim()}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.upload.tags')}
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {uploadTags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); }
              }}
              placeholder={t('documentStorage.actions.addTag')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">{t('documentStorage.upload.tagsHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.upload.selectFile')}
            </label>
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`relative rounded-lg border-2 border-dashed transition-all duration-200 ${
                isDragging
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 scale-[1.01]'
                  : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400'
              }`}
            >
              {uploadFile ? (
                <div className="flex items-center gap-3 px-4 py-3">
                  <span className="text-2xl flex-shrink-0">{getFileIcon(uploadFile.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{uploadFile.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(uploadFile.size)}</p>
                  </div>
                  <button
                    onClick={() => setUploadFile(null)}
                    className="p-1.5 rounded-lg text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 transition-colors flex-shrink-0"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center px-4 py-8 cursor-pointer">
                  <ArrowUpTrayIcon className={`w-8 h-8 mb-2 transition-colors ${isDragging ? 'text-indigo-500' : 'text-gray-400'}`} />
                  <p className={`text-sm text-center ${isDragging ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                    {isDragging ? t('documentStorage.upload.dropHere') : t('documentStorage.upload.dragHere')}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('documentStorage.upload.orClick')}</p>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setUploadFile(file);
                      if (file && !uploadTitle.trim()) {
                        setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowUploadModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleUpload}
              disabled={!uploadFile || !uploadTitle.trim() || uploading}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {uploading ? t('documentStorage.messages.loading') : t('documentStorage.actions.upload')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Folder Confirmation */}
      <Modal isOpen={showDeleteFolderModal} onClose={() => setShowDeleteFolderModal(false)} title={t('documentStorage.actions.delete')} size="sm">
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t('documentStorage.folders.deleteConfirm')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('documentStorage.folders.deleteWarning')}</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowDeleteFolderModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleDeleteFolder}
              className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('documentStorage.actions.delete')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Document Confirmation */}
      <Modal isOpen={showDeleteDocModal} onClose={() => setShowDeleteDocModal(false)} title={t('documentStorage.actions.delete')} size="sm">
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t('documentStorage.documents.deleteConfirm')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('documentStorage.documents.deleteWarning')}</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowDeleteDocModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleDeleteDoc}
              className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('documentStorage.actions.delete')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} title={previewDoc?.title || t('documentStorage.preview.title')} size="4xl">
        <div className="p-4">
          {previewDoc?.downloadUrl ? (
            <>
              {previewDoc.fileType?.startsWith('image/') ? (
                <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                  <img src={previewDoc.downloadUrl} alt={previewDoc.title} className="max-w-full max-h-[70vh] object-contain" />
                </div>
              ) : previewDoc.fileType === 'application/pdf' ? (
                <iframe src={previewDoc.downloadUrl} className="w-full h-[70vh] rounded-lg border border-gray-200 dark:border-gray-700" title={previewDoc.title} />
              ) : (
                <div className="text-center py-12">
                  <span className="text-5xl">{getFileIcon(previewDoc.fileType)}</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{t('documentStorage.preview.noPreview')}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('documentStorage.preview.noPreviewHint')}</p>
                  <button
                    onClick={() => handleDownload(previewDoc)}
                    className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    {t('documentStorage.actions.download')}
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatFileSize(previewDoc.fileSize)}</span>
                  {previewDoc.category?.name && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white" style={{ backgroundColor: previewDoc.category.color }}>
                      {previewDoc.category.name}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDownload(previewDoc)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  {t('documentStorage.preview.openInNewTab')}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('documentStorage.preview.noPreview')}</p>
            </div>
          )}
        </div>
      </Modal>

      {/* New Category Modal */}
      <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)} title={t('documentStorage.categories.create')} size="sm">
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.categories.name')}
            </label>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder={t('documentStorage.categories.namePlaceholder')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.categories.color')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={newCategoryColor}
                onChange={(e) => setNewCategoryColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
              />
              <div className="flex gap-1.5 flex-wrap">
                {['#6366f1', '#2563eb', '#16a34a', '#ca8a04', '#dc2626', '#7c3aed', '#6b7280'].map(color => (
                  <button
                    key={color}
                    onClick={() => setNewCategoryColor(color)}
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${newCategoryColor === color ? 'border-gray-400 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowCategoryModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim()}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {t('documentStorage.actions.save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Category Management Modal */}
      <Modal isOpen={showCategoryManageModal} onClose={() => { setShowCategoryManageModal(false); setEditingCategoryId(null); }} title={t('documentStorage.categories.title')} size="md">
        <div className="p-4 space-y-2">
          {categories.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">{t('documentStorage.upload.noCategory')}</p>
          ) : (
            categories.map(cat => (
              <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700">
                {editingCategoryId === cat.id ? (
                  <>
                    <input
                      type="color"
                      value={editingCategoryColor}
                      onChange={(e) => setEditingCategoryColor(e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border border-gray-300 dark:border-gray-600 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={editingCategoryName}
                      onChange={(e) => setEditingCategoryName(e.target.value)}
                      className="flex-1 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveEditCategory()}
                    />
                    <button
                      onClick={handleSaveEditCategory}
                      disabled={!editingCategoryName.trim()}
                      className="px-2 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {t('documentStorage.actions.save')}
                    </button>
                    <button
                      onClick={() => { setEditingCategoryId(null); setEditingCategoryName(''); }}
                      className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      {t('documentStorage.actions.cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    <span
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.color || '#6366f1' }}
                    />
                    <span className="flex-1 text-sm text-gray-900 dark:text-gray-100 truncate">{cat.name}</span>
                    <button
                      onClick={() => handleStartEditCategory(cat)}
                      className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-gray-400 hover:text-indigo-600 transition-colors"
                      title={t('documentStorage.actions.rename')}
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    {!cat.isDefault && (
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 transition-colors"
                        title={t('documentStorage.actions.delete')}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))
          )}
          <div className="pt-2">
            <button
              onClick={() => { setShowCategoryManageModal(false); setShowCategoryModal(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              {t('documentStorage.categories.create')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Document Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={t('documentStorage.edit.title')} size="md">
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.edit.titleField')}
            </label>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.edit.categoryField')}
            </label>
            <select
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('documentStorage.upload.noCategory')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.edit.tags')}
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {editTags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium">
                  {tag}
                  <button onClick={() => handleRemoveEditTag(tag)} className="hover:text-red-500">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={editTagInput}
              onChange={(e) => setEditTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleAddEditTag(); }
              }}
              placeholder={t('documentStorage.actions.addTag')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">{t('documentStorage.edit.tagsHint')}</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={!editTitle.trim()}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {t('documentStorage.actions.save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Move Document Modal */}
      <Modal isOpen={showMoveModal} onClose={() => setShowMoveModal(false)} title={t('documentStorage.move.title')} size="sm">
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.move.targetFolder')}
            </label>
            <select
              value={moveTargetFolder}
              onChange={(e) => setMoveTargetFolder(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('documentStorage.move.root')}</option>
              {renderFolderOptions(folderTree)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowMoveModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleSaveMove}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {t('documentStorage.actions.move')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation */}
      <Modal isOpen={showBulkDeleteModal} onClose={() => setShowBulkDeleteModal(false)} title={t('documentStorage.actions.delete')} size="sm">
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('documentStorage.actions.bulkDeleteConfirm').replace('{{count}}', String(selectedDocs.size))}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('documentStorage.documents.deleteWarning')}</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowBulkDeleteModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('documentStorage.actions.delete')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Move Folder Modal */}
      <Modal isOpen={showMoveFolderModal} onClose={() => setShowMoveFolderModal(false)} title={t('documentStorage.move.title')} size="sm">
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('documentStorage.move.targetFolder')}
            </label>
            <select
              value={moveFolderTarget}
              onChange={(e) => setMoveFolderTarget(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t('documentStorage.move.root')}</option>
              {folderTree
                .filter(node => node.id !== moveFolderNode?.id)
                .map(node => (
                  <option key={node.id} value={node.id}>{node.name}</option>
                ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowMoveFolderModal(false)}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('documentStorage.actions.cancel')}
            </button>
            <button
              onClick={handleSaveMoveFolder}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {t('documentStorage.actions.move')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DocumentLibrary;
